import type { BuyTreeNode, BuyTreeResponse, PriceListGroup, PurchasePriceList } from '@/types/buy';

// Разворачиваем дерево закупок в плоский список групп (базис + товар + год). Базис — это
// группировка моих офферов по адресу (не справочник), поэтому просто обходим узлы.
// Год берём из GOODS_ITEM (внутренний; в UI не показываем, но нужен для запроса детали).
export function flattenBuyTree(tree: BuyTreeResponse | undefined): PriceListGroup[] {
  const groups: PriceListGroup[] = [];
  if (!tree?.nodes) {
    return groups;
  }

  const walk = (nodes: BuyTreeNode[], basis?: BuyTreeNode): void => {
    for (const node of nodes) {
      if (node.type === 'BASIS_ITEM') {
        walk(node.childs ?? [], node);
      } else if (node.type === 'GOODS_ITEM' && basis && node.name && node.year != null) {
        groups.push({
          address: basis.address ?? '',
          addressTitle: basis.addressTitle ?? basis.name ?? '',
          basisId: basis.basisId,
          goods: node.name,
          year: node.year,
          updatedDate: node.updatedDate,
        });
      } else if (node.childs?.length) {
        walk(node.childs, basis);
      }
    }
  };

  walk(tree.nodes);
  return groups;
}

/** Базис с его прайс-листами — для списка «карточка-базис → строки-товары». */
export interface BasisGroup {
  readonly address: string;
  readonly addressTitle: string;
  readonly items: PriceListGroup[];
}

// Группировка плоского списка по базису (адрес + название). Порядок следования сохраняем
// как в дереве (бэкенд уже сортирует осмысленно).
export function groupByBasis(groups: readonly PriceListGroup[]): BasisGroup[] {
  const byKey = new Map<string, BasisGroup>();
  for (const group of groups) {
    const key = `${group.address}|${group.addressTitle}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.items.push(group);
    } else {
      byKey.set(key, { address: group.address, addressTitle: group.addressTitle, items: [group] });
    }
  }
  return [...byKey.values()];
}

/** Диапазон моих цен прайс-листа (по всем строкам и типам предоплаты); null — цен нет. */
export function priceRangeOf(list: PurchasePriceList): { min: number; max: number } | null {
  const prices = list.goodsList.flatMap((item) =>
    item.qualityRows.flatMap((row) =>
      Object.values(row.pricesByPrepaymentMap ?? {}).filter((price): price is number => price != null),
    ),
  );
  if (prices.length === 0) {
    return null;
  }
  return { min: Math.min(...prices), max: Math.max(...prices) };
}
